import React from "react";
import { Check, ChevronDown, ShieldCheck, Star } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  EventSearchFacet,
  EventSearchFilters,
} from "../../api/fetchEventSearchResults/types";

interface EventDirectoryFilterBarProps {
  facets: EventSearchFilters;
  showOnlyFavorites: boolean;
  onToggleFavorites: (checked: boolean) => void;
  showOnlyTotalAccess: boolean;
  onToggleTotalAccess: (checked: boolean) => void;
  selectedEventTypes: EventSearchFacet[];
  toggleEventType: (facet: EventSearchFacet) => void;
  selectedCategories: EventSearchFacet[];
  toggleCategory: (facet: EventSearchFacet) => void;
  selectedTrades: EventSearchFacet[];
  toggleTrade: (facet: EventSearchFacet) => void;
  selectedBusinessRoles: EventSearchFacet[];
  toggleBusinessRole: (facet: EventSearchFacet) => void;
}

function FilterButton({
  label,
  count,
  children,
}: {
  label: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm transition-colors ${
            count > 0
              ? "border-gray-400 dark:border-slate-500 bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white"
              : "border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700"
          }`}
          type="button"
        >
          <span>{label}</span>
          {count > 0 && (
            <span className="inline-flex items-center justify-center h-5 min-w-[20px] rounded-full bg-gray-900 dark:bg-white px-1.5 text-[11px] font-medium text-white dark:text-gray-900 leading-none">
              {count}
            </span>
          )}
          <ChevronDown className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-60 p-2">
        {children}
      </PopoverContent>
    </Popover>
  );
}

function FacetOptionList({
  items,
  selectedItems,
  onToggle,
}: {
  items: EventSearchFacet[];
  selectedItems: EventSearchFacet[];
  onToggle: (facet: EventSearchFacet) => void;
}) {
  if (items.length === 0) {
    return (
      <p className="px-2 py-3 text-sm text-gray-400 dark:text-gray-500">
        No options available
      </p>
    );
  }

  return (
    <div className="flex flex-col max-h-64 overflow-y-auto">
      {items.map((item) => {
        const isSelected = selectedItems.some((s) => s.name === item.name);
        return (
          <button
            className="flex items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors text-left hover:bg-gray-50 dark:hover:bg-slate-700/50"
            key={item.name}
            onClick={() => onToggle(item)}
            type="button"
          >
            <span
              className={`flex items-center justify-center h-4 w-4 rounded border flex-shrink-0 ${
                isSelected
                  ? "bg-gray-900 dark:bg-white border-gray-900 dark:border-white"
                  : "border-gray-300 dark:border-slate-600"
              }`}
            >
              {isSelected && (
                <Check className="h-3 w-3 text-white dark:text-gray-900" />
              )}
            </span>
            <span
              className={`flex-1 ${
                isSelected
                  ? "text-gray-900 dark:text-white"
                  : "text-gray-600 dark:text-gray-400"
              }`}
            >
              {item.name}
            </span>
            <span className="text-xs text-gray-400 dark:text-gray-500 tabular-nums">
              {item.eventCount}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default function EventDirectoryFilterBar({
  facets,
  showOnlyFavorites,
  onToggleFavorites,
  showOnlyTotalAccess,
  onToggleTotalAccess,
  selectedEventTypes,
  toggleEventType,
  selectedCategories,
  toggleCategory,
  selectedTrades,
  toggleTrade,
  selectedBusinessRoles,
  toggleBusinessRole,
}: EventDirectoryFilterBarProps) {
  return (
    <div className="flex flex-wrap items-center gap-2 mb-6">
      <button
        className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm transition-colors ${
          showOnlyFavorites
            ? "border-yellow-300 dark:border-yellow-600 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400"
            : "border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700"
        }`}
        onClick={() => onToggleFavorites(!showOnlyFavorites)}
        type="button"
      >
        <Star
          className={`h-3.5 w-3.5 ${showOnlyFavorites ? "fill-yellow-500 text-yellow-500" : ""}`}
        />
        <span>Favorites</span>
      </button>

      <button
        className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm transition-colors ${
          showOnlyTotalAccess
            ? "border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400"
            : "border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700"
        }`}
        onClick={() => onToggleTotalAccess(!showOnlyTotalAccess)}
        type="button"
      >
        <ShieldCheck className="h-3.5 w-3.5" />
        <span>Total Access</span>
      </button>

      {facets.eventTypes.length > 0 && (
        <FilterButton count={selectedEventTypes.length} label="Event Type">
          <FacetOptionList
            items={facets.eventTypes}
            onToggle={toggleEventType}
            selectedItems={selectedEventTypes}
          />
        </FilterButton>
      )}

      {facets.categories.length > 0 && (
        <FilterButton count={selectedCategories.length} label="Category">
          <FacetOptionList
            items={facets.categories}
            onToggle={toggleCategory}
            selectedItems={selectedCategories}
          />
        </FilterButton>
      )}

      {facets.trades.length > 0 && (
        <FilterButton count={selectedTrades.length} label="Trade">
          <FacetOptionList
            items={facets.trades}
            onToggle={toggleTrade}
            selectedItems={selectedTrades}
          />
        </FilterButton>
      )}

      {facets.businessRoles.length > 0 && (
        <FilterButton count={selectedBusinessRoles.length} label="Job Title">
          <FacetOptionList
            items={facets.businessRoles}
            onToggle={toggleBusinessRole}
            selectedItems={selectedBusinessRoles}
          />
        </FilterButton>
      )}
    </div>
  );
}
